/** @jsxImportSource @emotion/react */
import { faLayerGroup } from "@fortawesome/pro-regular-svg-icons";
import { useMemo, useState } from "react";
import {
    Modal, ModalBody, ModalFooter, ModalHeader
} from "reactstrap";
import { LAYER_TYPE } from "../../actions/Layers/constants";
import useConfig from "../../actions/Tenants/config/configHook";
import { useUserPreferences } from "../../hooks";
import { getFilledArrayOrDefault, isFilledArray } from "../../utils";
import MetaForm from "../Forms/MetaForm";
import { Button } from "./Button";

export const useGroupBy = (group_by_fields, default_value: string | null = null) => {
    const config = useConfig();
    const [userPreferences, updatePreferences] = useUserPreferences();

    // TODO: This is causing double loading of layer list when switching tabs
    const groupByArray = useMemo(
        () => (userPreferences.group_by ?? [...(default_value?.split(",") ?? [])].filter((v) => !!v)).filter((v) => group_by_fields.find((i) => i.value === v)),
        [userPreferences?.group_by, group_by_fields]
    );

    const groupBy = useMemo(() => groupByArray.join(","), [groupByArray]);

    // The stored key is shared between screens that each whitelist a different
    // subset, so keep selections this screen doesn't own.
    const updateGroupBy = (value: any) => {
        const outOfScope = getFilledArrayOrDefault(userPreferences.group_by)
            .filter((v) => !group_by_fields.find((i) => i.value === v));
        updatePreferences({ group_by: [...outOfScope, ...getFilledArrayOrDefault(value)] });
    };


    const getGroupByLabel = (key: string) => group_by_fields.find((i) => i.value === key)?.label;

    const getGroups = (layers: any, filter_type = LAYER_TYPE.PALLET) => {
        const filteredLayers = layers.filter((i) => i.type === filter_type || !filter_type);

        if (!isFilledArray(groupByArray)) {
            return [false, filteredLayers];
        }

        const layersWithGroupKey = filteredLayers.map((layer: any) => {
            const keysWithValue = groupByArray.filter((k) => layer[k] !== undefined);

            if (keysWithValue.length === 0) {
                return {
                    ...layer,
                    group_key: "",
                    group_label: "",
                };
            }

            const group_key = keysWithValue.map((k) => `${k}:${layer[k]}`).join(",");
            const group_label = keysWithValue.map((k) => `${config.get_form_option_label(k, layer[k])}`).join(" | ");

            return {
                ...layer,
                group_key,
                group_label,
            };
        });

        const grouped = layersWithGroupKey.reduce((acc, item) => {
            const key = item.group_key;
            if (!acc[key]) {
                acc[key] = {
                    group_key: key,
                    group_label: item.group_label,
                    items: [],
                    pallets: 0,
                    boxes: 0,
                };
            }

            acc[key].pallets++;
            acc[key].items.push(item);
            acc[key].boxes += item.number_of_boxes || 0;

            return acc;
        }, {});

        return [Object.values(grouped), layersWithGroupKey];
    };

    return { groupBy, groupByArray, getGroupByLabel, updateGroupBy, group_by_fields, getGroups };
};

type GroupByModalProps = {
    title?: string;
    description?: string;
    groupByProps: {
        groupBy?: string | null;
        groupByArray?: string[];
        updateGroupBy: (value: any) => void;
        getGroupByLabel: (key: string) => string | undefined;
        group_by_fields?: any[];
    };
    isInBottomBar?: boolean;
    extraContent?: React.ReactNode;
};

const GroupByModal = ({
    title = "Count pallets",
    description = "Count pallets and boxes and group by the following fields",
    groupByProps,
    isInBottomBar,
    extraContent
}: GroupByModalProps) => {
    const config = useConfig();

    const [modalOpen, setModalOpen] = useState(false);

    const { groupBy, groupByArray, updateGroupBy, getGroupByLabel, group_by_fields } = groupByProps;

    const toggleModal = () => {
        setModalOpen(!modalOpen);
    };

    if (!isFilledArray(group_by_fields)) {
        return null;
    }

    const form = [{
        label: "Group by",
        name: "group_by",
        type: "checkbox",
        options: group_by_fields,
        return_array: true
    }];

    return (
        <>
            <Button color="light" onClick={toggleModal} icon={faLayerGroup} isInBottomBar={isInBottomBar}>
                {getFilledArrayOrDefault(groupByArray).map((i) => getGroupByLabel(i)).filter(Boolean).join(", ") || "groups"}
            </Button>

            <Modal isOpen={modalOpen} size="xl">
                <ModalHeader>{title}</ModalHeader>

                <ModalBody>
                    <p>{description}</p>

                    <MetaForm
                        config={config}
                        meta={form}
                        object={{ group_by: groupBy }}
                        setValue={(_, value: any) => updateGroupBy(value)}
                    />
                    {extraContent}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={() => updateGroupBy([])}>
                        Deselect All
                    </Button>

                    <Button color="primary" onClick={toggleModal}>Close</Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default GroupByModal;
